import React, { FunctionComponent, useMemo, useRef, useState } from 'react';
import { Set } from 'immutable';
import { animated } from '@react-spring/web';
import { Spinner } from 'record-it-components/misc/Spinner/Spinner';
import { Fade } from 'record-it-components/animation/Fade/Fade';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { Disposable } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import {
  PageLoadingOverlayContext,
  PageLoadingOverlayContextValue
} from '../context/PageLoadingOverlayContext';
import styles from './AppPageLoadingOverlayContextProvider.module.css';

const defaultLoadingContext = Symbol('defaultLoadingContext');

export const AppPageLoadingOverlayContextProvider: FunctionComponent =
  React.memo((props) => {
    const { children } = props;

    const lastMessageId = useRef(0);

    // The default loading context is here so we immediately render the loading overlay.
    // It will get removed as soon as something starts to use PageLoader.
    // This is a workaround so we have a fluid start. Without this, the rendering of the loading overlay would be delayed, probably because react is so busy with rendering everything?
    const [loadingContexts, setLoadingContexts] = useState(
      Set<symbol>([defaultLoadingContext])
    );
    const [loadingMessages, setLoadingMessages] = useState<
      Array<{ message: string; id: number }>
    >([]);

    const removeDefaultLoadingContextDelayed = useEventCallback(() => {
      setTimeout(() => {
        setLoadingContexts((oldLoadingContexts) => {
          if (!oldLoadingContexts.includes(defaultLoadingContext)) {
            return oldLoadingContexts;
          }

          return oldLoadingContexts.delete(defaultLoadingContext);
        });
      }, 300);
    });

    const addLoadingMessage = useEventCallback((message: string): void => {
      setLoadingMessages((oldLoadingMessages) => {
        const newLoadingMessages = [
          ...oldLoadingMessages,
          {
            id: ++lastMessageId.current,
            message
          }
        ];

        return newLoadingMessages.slice(
          Math.max(newLoadingMessages.length - 5, 0)
        );
      });
    });

    const startLoading = useEventCallback((): Disposable => {
      const context = Symbol('loadingContext');

      let newLoadingContexts = loadingContexts.add(context);

      if (loadingContexts.count() === 0) {
        // add the defaultLoading context when starting a new instance of the loading overlay, to show it for at least a minimum time
        newLoadingContexts = newLoadingContexts.add(defaultLoadingContext);
        setLoadingMessages([]);
      }

      setLoadingContexts(newLoadingContexts);
      removeDefaultLoadingContextDelayed();

      return {
        dispose: () => {
          setLoadingContexts((oldValue) => oldValue.delete(context));
        }
      };
    });

    const contextValue = useMemo<PageLoadingOverlayContextValue>(() => {
      return {
        addLoadingMessage,
        startLoading
      };
    }, [addLoadingMessage, startLoading]);

    return (
      <PageLoadingOverlayContext.Provider value={contextValue}>
        {children}
        <Fade
          show={loadingContexts.count() > 0}
          showImmediately={true}
        >
          {(style) => (
            <animated.div
              className={styles.loadingOverlay}
              style={style}
            >
              <div className={styles.messagesContainer}>
                {loadingMessages.map((m) => (
                  <div
                    key={m.id}
                    className={styles.message}
                  >
                    {m.message}
                  </div>
                ))}
              </div>

              <Spinner className={styles.spinner}></Spinner>
            </animated.div>
          )}
        </Fade>
      </PageLoadingOverlayContext.Provider>
    );
  });
